import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { GoodsReferenceType } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import { blankGoodsReference } from 'features/declaration/models/blankItems'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormFieldArray from 'components/p5/FormFieldArray'
import FormField from 'components/p5/FormField'
import FormSelect from 'components/p5/FormSelect'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'
import { useFetchConsignmentItems } from 'features/declaration/consignment-item/api/fetchConsignmentItems'
import { useDeclarationEditorContext } from 'features/declaration/form/contexts/DeclarationEditorContext'
import { ConsignmentItem } from 'features/declaration/consignment-item/models/types'

const getConsignmentItemLabel = (consignmentItem: ConsignmentItem, index: number) => `${index + 1}. ${
  (consignmentItem.commodityDescriptionOfGoods && consignmentItem.commodityDescriptionOfGoods?.length > 20)
    ? `${consignmentItem.commodityDescriptionOfGoods.substring(0, 20)}…` : consignmentItem.commodityDescriptionOfGoods ?? ''
}`

function GoodsReferences({ transportEquipmentIndex } : { transportEquipmentIndex: number }) {
  const scope = `transportEquipment.${transportEquipmentIndex}.goodsReferences` as const
  const { t } = useTranslation()

  const { declarationData } = useDeclarationEditorContext()
  const consignmentId = declarationData?.consignment?.id ?? null

  const { control } = useFormContext<TransitOperationFormValues>()

  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<TransitOperationFormValues, GoodsReferenceType>({
    control,
    name: scope,
  })

  const { data: consignmentItems } = useFetchConsignmentItems({ consignmentId })

  const declarationGoodsItemNumberOptions = useMemo(
    () => (consignmentItems ?? []).map((consignmentItem, index) => ({
      label: getConsignmentItemLabel(consignmentItem, index),
      value: consignmentItem.id,
    })),
    [consignmentItems],
  )

  const paths = useMemo(() => [
    scope,
    ...visibleFields.map((
      f,
    ) => `${scope}.${f.index}.consignmentItemId`),
  ], [scope, visibleFields])

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', paths)
  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-transport-equipment-goods-references"
        headingLabel={t('declaration.p5.goodsReferences')}
        fieldRule={fieldRequirements[scope]}
        collapsible={false}
      >
        {
          visibleFields.map((fieldWithIndex) => {
            const goodsReferenceItem = fieldWithIndex.field
            const innerItemScope = `${scope}.${fieldWithIndex.index}` as const

            return (
              <FormFieldArray
                key={`goodsReferenceItem_${goodsReferenceItem.id}`}
                titleKey="declaration.p5.sequenceNumber"
                removeItem={() => softRemove(fieldWithIndex.index)}
                hidden={goodsReferenceItem.deleted}
                sequenceNumber={goodsReferenceItem.sequenceNumber + 1}
              >
                <FormField
                  labelKey="declaration.p5.declarationGoodsItemNumber"
                  fieldRule={fieldRequirements[`${innerItemScope}.consignmentItemId`]}
                >
                  <FormSelect
                    <TransitOperationFormValues>
                    field={`${innerItemScope}.consignmentItemId`}
                    labelKey="declaration.p5.declarationGoodsItemNumber"
                    options={declarationGoodsItemNumberOptions}
                    type="sync"
                    emptyValue={null}
                    fieldRule={fieldRequirements[`${innerItemScope}.consignmentItemId`]}
                  />

                </FormField>
              </FormFieldArray>
            )
          })
        }
      </FormCollapsibleColumn>

      <div className="row">
        <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
          <button
            type="button"
            className="btn btn-sm btn-primary mt-2"
            onClick={() => appendWithSequence(blankGoodsReference)}
            disabled={isAddForbidden}
          >
            <i className="fa fa-plus me-2" />
            {t('declaration.p5.addNewGoodsReferences')}
          </button>
        </div>
      </div>
    </section>
  )
}

export default GoodsReferences
